/**
 * useTrudChat Hook
 * Custom wrapper around @ai-sdk/react's useChat hook
 * Integrates with Zustand store and trd-agent backend
 *
 * SIMPLIFIED VERSION - Trusts AI SDK for streaming
 */

import { useChat as useAIChat } from "@ai-sdk/react";
import { DefaultChatTransport } from "ai";
import type { UIMessage } from "ai";
import { useEffect, useState, useRef } from "react";
import { useChatStore } from "@/stores/chatStore";
import { useToast } from "@/hooks/use-toast";
import { getChatConfig, getChatConfigAsync } from "@/utils/chat-config";
import { useAuthUser } from "@/context/auth/AuthContext";
import { useQueryClient } from "react-query";
import { chatKeys } from "./react-query/chat/useChatPersistence";
import type { AgentType, ChatMessage } from "@/types/chat.types";

interface UseTrudChatOptions {
  conversationId?: string | null; // Real backend conversation ID (null for new conversations)
  messages?: UIMessage[]; // History messages from backend
  routingAgent?: AgentType; // Explicit routing
  onConversationCreated?: (conversationId: string) => void; // Callback when conversation is created
}

/**
 * Custom hook for chat functionality
 * Wraps AI SDK's useChat and integrates with our backend
 */
export const useTrudChat = (options: UseTrudChatOptions) => {
  const {
    conversationId,
    messages: historyMessages = [],
    routingAgent,
  } = options;
  const store = useChatStore();
  const { toast } = useToast();
  const user = useAuthUser();
  const queryClient = useQueryClient();

  // Get token from store on mount
  const [token, setToken] = useState<string>("");
  const [isTokenReady, setIsTokenReady] = useState(false);

  // ✅ Track conversation ID using ref (no stale closure issues)
  const backendConversationIdRef = useRef<string | null>(null);

  // Handle conversationId changes - update ref
  useEffect(() => {
    if (!conversationId) {
      backendConversationIdRef.current = null;
    } else {
      // Use the conversationId directly if provided
      backendConversationIdRef.current = conversationId;
    }
  }, [conversationId]);

  useEffect(() => {
    const loadToken = async () => {
      const config = await getChatConfigAsync(user);
      setToken(config.token);
      setIsTokenReady(!!config.token);
    };
    loadToken();
  }, [user]);

  // Get chat configuration (handles dev vs prod)
  const config = getChatConfig(user, token);

  // Intercept fetch to add auth headers AND filter invalid events
  useEffect(() => {
    const originalFetch = window.fetch;

    window.fetch = async function (
      input: RequestInfo | URL,
      init?: RequestInit
    ): Promise<Response> {
      const inputStr =
        typeof input === "string"
          ? input
          : input instanceof URL
          ? input.href
          : input.url;

      // Sólo interceptar el chat del agent (ahora vía el PEP del BFF, #125).
      if (inputStr.includes("/api/v1/agent/chat")) {
        // Token de identity; el BFF afirma businessId/userId del token (no se manda).
        const headers = new Headers(init?.headers || {});
        headers.set("Authorization", `Bearer ${config.token}`);

        // ✅ Modify body to include conversationId dynamically
        let modifiedBody = init?.body;
        if (init?.body && typeof init.body === "string") {
          try {
            const bodyData = JSON.parse(init.body);
            // Add conversationId if we have one
            if (backendConversationIdRef.current) {
              bodyData.conversationId = backendConversationIdRef.current;
            }
            modifiedBody = JSON.stringify(bodyData);
          } catch (e) {
            console.error("Failed to parse request body:", e);
          }
        }

        const response = await originalFetch(input, {
          ...init,
          headers,
          body: modifiedBody,
        });

        // Capture response headers for conversation ID and agent type
        const conversationIdHeader = response.headers.get("X-Conversation-Id");
        const agentTypeHeader = response.headers.get(
          "X-Agent-Type"
        ) as AgentType | null;

        // ✅ Update conversation ID ref if backend sends it
        if (conversationIdHeader) {
          backendConversationIdRef.current = conversationIdHeader;

          // Call the callback if provided and ID changed
          if (
            options.onConversationCreated &&
            conversationIdHeader !== conversationId
          ) {
            options.onConversationCreated(conversationIdHeader);
          }
        }

        // ✅ Update current agent type
        if (agentTypeHeader) {
          store.setCurrentAgentType(agentTypeHeader);
        }

        // ✅ Filter out invalid "finish" event from stream
        if (response.body) {
          let buffer = "";
          const transformStream = new TransformStream({
            transform(chunk, controller) {
              buffer += new TextDecoder().decode(chunk, { stream: true });
              const lines = buffer.split("\n");
              buffer = lines.pop() || "";

              for (const line of lines) {
                if (line.startsWith("data: ")) {
                  const data = line.slice(6);
                  if (data.trim() === "[DONE]") {
                    controller.enqueue(new TextEncoder().encode(line + "\n"));
                    continue;
                  }

                  try {
                    const parsed = JSON.parse(data);
                    // Skip the invalid "finish" event
                    if (parsed.type === "finish" && parsed.finishReason) {
                      continue;
                    }
                  } catch {
                    // Not JSON, keep it
                  }
                }
                controller.enqueue(new TextEncoder().encode(line + "\n"));
              }
            },
            flush(controller) {
              if (buffer) {
                controller.enqueue(new TextEncoder().encode(buffer));
              }
            },
          });

          const transformedBody = response.body.pipeThrough(transformStream);
          return new Response(transformedBody, {
            status: response.status,
            statusText: response.statusText,
            headers: response.headers,
          });
        }

        return response;
      }

      return originalFetch(input, init);
    };

    return () => {
      window.fetch = originalFetch;
    };
  }, [config.token, config.businessId, store]);

  // ✅ Use DefaultChatTransport as per AI SDK v6 docs
  const chat = useAIChat({
    transport: new DefaultChatTransport({
      api: `${config.baseUrl}/chat`, // BFF PEP: {VITE_BFF_URL}/api/v1/agent/chat (#125)
      body: {
        // ✅ Don't include conversationId here - it's added dynamically in fetch interceptor
        ...(routingAgent && { metadata: { routingAgent } }),
      },
    }),
    messages: historyMessages, // ✅ Load history messages (AI SDK v6)

    onFinish: () => {
      store.setStreaming(false);
      store.setGenerating(false);

      // ✅ Invalidar conversación actual para marcar como stale
      const currentId = backendConversationIdRef.current;
      if (currentId) {
        queryClient.invalidateQueries({
          queryKey: chatKeys.conversation(currentId),
        });
      }

      // ✅ Auto-refresh conversations list in sidebar
      queryClient.invalidateQueries({
        queryKey: chatKeys.conversations(),
      });
    },
    onError: (error) => {
      console.error("❌ onError called:", error);
      store.setStreaming(false);
      store.setGenerating(false);

      toast({
        title: "Error",
        description: "Ocurrió un error al enviar el mensaje",
        variant: "error",
        duration: 5000,
      });

      console.error("Chat error:", error);
    },
  });

  // ✅ Reset messages when conversation changes or history loads
  useEffect(() => {
    if (historyMessages.length > 0 || conversationId === null) {
      chat.setMessages(historyMessages);
    }
  }, [conversationId, historyMessages.length]); // Update when conversation or history changes

  // Sync loading state with store
  useEffect(() => {
    const isStreaming = chat.status === "streaming";
    const isLoading = chat.status === "submitted" || isStreaming;

    store.setStreaming(isStreaming);
    store.setGenerating(isLoading);
  }, [chat.status]); // ❌ Remove 'store' to prevent infinite loop

  // Log errors
  useEffect(() => {
    if (chat.error) {
      console.error("❌ Chat error:", chat.error);
    }
  }, [chat.error]);

  // Custom send message function
  const sendMessage = (content: string) => {
    if (!content.trim()) return;

    // Add message immediately for instant feedback
    chat.setMessages([...chat.messages]);

    store.setGenerating(true);
    store.setStreaming(true);

    try {
      // AI SDK will handle the request and sync messages
      chat.sendMessage({
        parts: [{ type: "text", text: content }],
      });
    } catch (error) {
      console.error("❌ Error calling chat.sendMessage:", error);
      store.setGenerating(false);
      store.setStreaming(false);
    }
  };

  // Stop generation function
  const stopGeneration = () => {
    chat.stop();
    store.setStreaming(false);
    store.setGenerating(false);
  };

  // Reload messages function
  const reload = () => {
    if (chat.regenerate) {
      chat.regenerate();
      store.setGenerating(true);
      store.setStreaming(true);
    }
  };

  return {
    messages: chat.messages as ChatMessage[], // ✅ Trust AI SDK - it updates in real-time (cast to ChatMessage for compatibility)
    setMessages: chat.setMessages, // ✅ Available if needed (AI SDK handles user messages automatically)
    sendMessage,
    stopGeneration,
    reload,
    isLoading: chat.status === "submitted" || chat.status === "streaming",
    isStreaming: chat.status === "streaming",
    isGenerating: store.isGenerating,
    currentAgentType: store.currentAgentType,
    error: chat.error,
    isTokenReady, // ✅ Expose token ready state
  };
};
